import React from 'react';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  CHART_COLOR,
  CHART_SERIES_COLORS,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/constants';
import { TruncatedXAxisTick } from '@/features/ai-agents/chat/MarkdownTableVisualization/helpers';
import {
  ChartRow,
  ChartSeries,
  ChartValueFormatter,
  CompareLineChartData,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/types';
import {
  createVariantLineChartData,
  getChartXAxisInterval,
  sortChartRowsByDate,
} from '@/features/ai-agents/chat/MarkdownTableVisualization/utils';

interface BarChartProps {
  compareData?: CompareLineChartData | null;
  data: ChartRow[];
  formatValue: ChartValueFormatter;
  series?: ChartSeries[];
  valueLabel: string;
}

export function BarChart({
  compareData,
  data,
  formatValue,
  series = [],
  valueLabel,
}: BarChartProps) {
  if (compareData) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <RechartsBarChart
          data={compareData.rows}
          margin={{ top: 12, right: 20, bottom: 8, left: 0 }}
        >
          <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={<TruncatedXAxisTick centered />}
            interval={getChartXAxisInterval(compareData.rows.length)}
            height={72}
          />
          <YAxis tickFormatter={formatValue} />
          <Tooltip formatter={formatValue} />
          <Legend />
          {compareData.series.map((chartSeries, index) => (
            <Bar
              key={chartSeries.dataKey}
              dataKey={chartSeries.dataKey}
              name={chartSeries.name}
              fill={CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]}
              radius={[3, 3, 0, 0]}
              isAnimationActive={false}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    );
  }

  const groupedChartData = createVariantLineChartData(series);
  const hasMultipleSeries = groupedChartData.series.length > 0;
  const chartData = hasMultipleSeries
    ? sortChartRowsByDate(groupedChartData.rows)
    : sortChartRowsByDate(data);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <RechartsBarChart
        data={chartData}
        margin={{ top: 12, right: 20, bottom: 8, left: 0 }}
      >
        <CartesianGrid stroke="#e5e7eb" strokeDasharray="3 3" />
        <XAxis
          dataKey="x"
          tick={<TruncatedXAxisTick centered />}
          interval={getChartXAxisInterval(chartData.length)}
          height={72}
        />
        <YAxis tickFormatter={formatValue} />
        <Tooltip formatter={formatValue} />
        {hasMultipleSeries && <Legend />}
        {hasMultipleSeries ? (
          groupedChartData.series.map((chartSeries, index) => (
            <Bar
              key={chartSeries.dataKey}
              dataKey={chartSeries.dataKey}
              name={chartSeries.name}
              fill={CHART_SERIES_COLORS[index % CHART_SERIES_COLORS.length]}
              radius={[3, 3, 0, 0]}
              isAnimationActive={false}
            />
          ))
        ) : (
          <Bar
            dataKey="y"
            name={valueLabel}
            fill={CHART_COLOR}
            radius={[3, 3, 0, 0]}
            isAnimationActive={false}
          />
        )}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}
